
import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Box, useTheme, useMediaQuery, ThemeProvider } from '@mui/material';
import Validate from '../../../../app/validation/validation';
import { TextInputHot, MODE } from 'components/common/TextInputHot';
import { ButtonHot } from 'components/common/ButtonHot';
import { TextHot } from 'components/common/TextHot';
import { useGenerateOtpMutation } from '../../../../redux/api/users.api';
import { executeWithCaptcha, generateTestId } from '../../../../app/functions';
import { setLoginReturn, setLoginCode } from '../../../../redux/actions';
import Recaptcha from '../../../../components/services/google/recaptcha';
import { RECAPTCHA_ACTIONS } from 'components/services/google/recaptcha';
import { appTheme } from 'common/theme';
import { padId } from 'pages/personal-zone/utils/functions';
import { OTP_PROCESS } from 'pages/personal-zone/utils/enums';

const LoginForm = ({ 
    text, 
    setUserId, 
    setPhone, 
    setLoginValidationFailed, 
    setLoginValidationFailedMsg, 
    loginValidationFailed, 
    loginValidationFailedMsg, 
    onLogin,
    onOtpRequested,
    testID = 'login-form' 
}) => {
    const dispatch = useDispatch();
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
    
    const loginId = useSelector((state) => state.data.userLoginId);
    const [generateOtpMutation, { isLoading: isGenerateOtpLoading }] = useGenerateOtpMutation();

    const [isLoading, setIsLoading] = useState(false);
    const [firstTry, setFirstTry] = useState(true);

    const [form, setForm] = useState({
        user_id: {
            val: loginId || '',
            valid: false,
            rules: ['not_empty', 'user_id', 'not_empty'],
            errMsg: '',
            placeholder: text?.['login_id_placeholder'] || 'הזן מספר זהות',
        },
        phone: {
            val: '',
            valid: false,
            rules: ['not_empty', 'phone', 'not_empty'],
            errMsg: '',
            placeholder: text?.['login_phone_placeholder'] || 'הזן מספר טלפון',
        },
    });

    useEffect(() => {
        if (loginId) {
            setForm(prev => ({
                ...prev,
                user_id: { ...prev.user_id, val: loginId }
            }));
        }
    }, [loginId]);

    const handleInputChange = (evt) => {
        const { name, value } = evt.target;
        const validateResult = Validate(value, form[name].rules);

        setForm((prevForm) => ({
            ...prevForm,
            [name]: { 
                ...prevForm[name], 
                val: value, 
                valid: validateResult.valid, 
                errMsg: validateResult.msg 
            }
        }));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        if (isLoading || isGenerateOtpLoading) return;

        dispatch(setLoginCode(0));

        let form_valid = true;
        const new_form = { ...form };

        for (let field in form) {
            const validateResult = Validate(form[field].val, form[field].rules);
            new_form[field].valid = validateResult.valid;
            new_form[field].errMsg = validateResult.msg;
            if (!validateResult.valid) form_valid = false;
        }

        setForm(new_form);
        setFirstTry(false);

        if (!form_valid) return;

        setIsLoading(true);
        
        executeWithCaptcha(async (token) => {
            const payload = {
                'g-recaptcha-response': token,
                userId: padId(form.user_id.val),
                cellphone: String(form.phone.val),
                process: OTP_PROCESS.LOGIN,
            };
            
            try {
                const result = await generateOtpMutation(payload).unwrap();
                
                dispatch(setLoginReturn({ result: 'Success' }));

                if (setPhone) setPhone(form.phone.val);
                if (setUserId) setUserId(form.user_id.val);

                if (onOtpRequested) {
                    onOtpRequested({
                        initialOtpResponse: result,
                        phone: form.phone.val,
                        userId: form.user_id.val,
                    });
                }
            } catch (error) {
                if (setLoginValidationFailed) setLoginValidationFailed(true);
                if (setLoginValidationFailedMsg) {
                    setLoginValidationFailedMsg(
                        error?.data || text?.['login_generic_err'] || 'שגיאה בשליחת קוד אימות, אנא נסה שוב'
                    );
                }
            } finally {
                setIsLoading(false);
            }
        }, RECAPTCHA_ACTIONS.VERIFY_CODE);
    };

    const user_id = form.user_id;
    const phone = form.phone;
    const userIdHasError = !firstTry && !user_id.valid;
    const phoneHasError = !firstTry && !phone.valid;

    return (
        <ThemeProvider theme={appTheme}>
            <Box 
                component="form" 
                onSubmit={handleSubmit} 
                testID={generateTestId(testID, 'form')} 
                sx={{ width: '100%', display: 'flex', flexDirection: 'column' }}
            >
                <TextInputHot
                    name="user_id"
                    mode={MODE.FORM}
                    label={text?.['login_id_label'] || 'תעודת זהות'}
                    placeholder={text?.['login_id_placeholder'] || 'הזן מספר זהות'}
                    value={padId(user_id.val)}
                    onChange={handleInputChange}
                    disabled={!!loginId}
                    error={userIdHasError}
                    helperText={userIdHasError ? user_id.errMsg : ''}
                    testID={generateTestId(testID, 'user-id-input')}
                />

                <TextInputHot
                    name="phone"
                    mode={MODE.FORM}
                    label={text?.['login_phone_label'] || 'טלפון'}
                    placeholder={text?.['login_phone_placeholder'] || 'הזן מספר טלפון'}
                    value={phone.val}
                    onChange={handleInputChange}
                    error={phoneHasError}
                    helperText={phoneHasError ? phone.errMsg : ''}
                    testID={generateTestId(testID, 'phone-input')}
                />

                <Box sx={{ display: 'flex', justifyContent: 'center', mt: 3 }}>
                    <ButtonHot
                        type="submit"
                        mode="contained"
                        size={isMobile ? 'l' : 'xl'}
                        disabled={isLoading || isGenerateOtpLoading}
                        loading={isLoading || isGenerateOtpLoading}
                        testID={generateTestId(testID, 'submit-button')}
                    >
                        {text?.['login_log_in'] || 'התחבר'}
                    </ButtonHot>
                </Box>

                {loginValidationFailed && (
                    <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
                        <TextHot 
                            size="m"
                            color="error"
                            testID={generateTestId(testID, 'error-message')}
                            sx={{ textAlign: 'center' }}
                        >
                            {loginValidationFailedMsg}
                        </TextHot>
                    </Box>
                )}

                <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
                    <TextHot 
                        size="m"
                        color="secondary"
                        testID={generateTestId(testID, 'sms-message')}
                        sx={{ textAlign: 'center' }}
                    >
                        {text?.['registration_form_sms_msg'] || 'הודעת SMS תישלח למספר הטלפון שלך'}
                    </TextHot>
                </Box>
                
                <Box sx={{ mt: 2 }} testID={generateTestId(testID, 'recaptcha-container')}>
                    <Recaptcha isFunction={true} enterprise={true} onReady={() => {}} />
                </Box>
            </Box>
        </ThemeProvider>
    );
};

export default LoginForm;
